import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from './api';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './config';

interface SearchResult {
  products?: any[];
  categories?: any[];
  suppliers?: any[];
  invoices?: any[];
  sales?: any[];
  users?: any[];
}

interface SearchComponentProps {
  onClose: () => void;
}

function SearchComponent({ onClose }: SearchComponentProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult>({});
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1); // للتنقل بالأسهم
  const [allResults, setAllResults] = useState<any[]>([]); // جميع النتائج للتنقل
  
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMounted = useRef(true);
  const navigate = useNavigate();

  const getAuthHeader = useCallback(() => {
  const token = sessionStorage.getItem('token');
  if (token) {
    console.log('✅ Sending token to App');
    return { Authorization: `Bearer ${token}` };
  }
  console.log('❌ No token found in App');
  return {};
}, []);

  // تنظيف عند إزالة المكون
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // تحميل عمليات البحث الأخيرة
    const saved = localStorage.getItem('recent_searches');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setRecentSearches(parsed.slice(0, 5));
      } catch (error) {
        console.error('خطأ في تحميل عمليات البحث الأخيرة:', error);
      }
    }
    
    // التركيز على حقل الإدخال
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // إغلاق النافذة عند الضغط على Escape
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // إغلاق النافذة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // تجميع كل النتائج في مصفوفة واحدة للتنقل
  useEffect(() => {
    const all: any[] = [];
    
    if (results.products) {
      all.push(...results.products.map(item => ({ ...item, type: 'product' })));
    }
    if (results.categories) {
      all.push(...results.categories.map(item => ({ ...item, type: 'category' })));
    }
    if (results.suppliers) {
      all.push(...results.suppliers.map(item => ({ ...item, type: 'supplier' })));
    }
    if (results.invoices) {
      all.push(...results.invoices.map(item => ({ ...item, type: 'invoice' })));
    }
    if (results.sales) {
      all.push(...results.sales.map(item => ({ ...item, type: 'sale' })));
    }
    if (results.users) {
      all.push(...results.users.map(item => ({ ...item, type: 'user' })));
    }
    
    setAllResults(all);
    setSelectedIndex(-1); // إعادة تعيين التحديد عند تغيير النتائج
  }, [results]);

  // التنقل بالأسهم
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!allResults.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev < allResults.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        handleResultClick(allResults[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [allResults, selectedIndex]);

  // تمرير العنصر المحدد إلى view
  useEffect(() => {
    if (selectedIndex >= 0) {
      const selectedElement = document.getElementById(`search-item-${selectedIndex}`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  const showError = useCallback((message: string) => {
    // يمكن استخدام toast أو أي مكتبة أخرى
    console.error(message);
  }, []);

  // دالة البحث مع debounce محسن
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults({});
      return;
    }

    setLoading(true);
    try {
      const url = selectedType 
        ? `${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}&type=${selectedType}`
        : `${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}`;
      
      const response = await api.get(url, {
        headers: getAuthHeader(),
        timeout: 5000
      });
      
      if (!isMounted.current) return;
      
      setResults(response.data);
      
      // حفظ البحث في قائمة الأخيرة (فقط إذا كان طويل كفاية)
      if (searchQuery.length >= 3) {
        setRecentSearches(prev => {
          const updated = [searchQuery, ...prev.filter(s => s !== searchQuery)].slice(0, 5);
          localStorage.setItem('recent_searches', JSON.stringify(updated));
          return updated;
        });
      }
      
    } catch (error: any) {
      console.error('خطأ في البحث:', error);
      if (isMounted.current) {
        showError('حدث خطأ في البحث');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [selectedType, getAuthHeader, showError]);

  // معالج تغيير النص مع debounce (400ms)
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    
    // إلغاء الـ timeout السابق
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // تعيين timeout جديد
    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value);
      }, 400);
    } else if (value.length === 0) {
      setResults({});
      setAllResults([]);
    }
  }, [performSearch]);

  const handleTypeChange = useCallback((type: string) => {
    setSelectedType(type);
    if (query.length >= 2) {
      performSearch(query);
    }
  }, [query, performSearch]);

  const handleResultClick = useCallback((item: any) => {
    switch (item.type) {
      case 'product':
        navigate(`/admin?product=${item.id}`);
        break;
      case 'category':
        navigate(`/categories?category=${item.id}`);
        break;
      case 'supplier':
        navigate(`/suppliers?supplier=${item.id}`);
        break;
      case 'invoice':
        navigate(`/invoice/${item.id}`);
        break;
      case 'sale':
        navigate(`/sale/${item.id}`);
        break;
      case 'user':
        navigate(`/settings?user=${item.id}`);
        break;
      default:
        return;
    }
    onClose();
  }, [navigate, onClose]);

  const handleRecentSearchClick = useCallback((search: string) => {
    setQuery(search);
    performSearch(search);
  }, [performSearch]);

  const getTypeIcon = useCallback((type: string) => {
    const icons: { [key: string]: string } = {
      product: '📦',
      category: '🏷️',
      supplier: '🤝',
      invoice: '📄',
      sale: '💰',
      user: '👤'
    };
    return icons[type] || '📌';
  }, []);

  const getTypeName = useCallback((type: string) => {
    const names: { [key: string]: string } = {
      product: 'منتج',
      category: 'تصنيف',
      supplier: 'مورد',
      invoice: 'فاتورة شراء',
      sale: 'فاتورة بيع',
      user: 'مستخدم'
    };
    return names[type] || type;
  }, []);

  const getItemPrice = useCallback((item: any) => {
    if (item.type === 'product') return item.price;
    if (item.type === 'sale') return item.total;
    if (item.type === 'invoice') return item.total;
    return null;
  }, []);

  const getItemStatus = useCallback((item: any) => {
    if (item.type === 'product') return item.stock;
    if (item.type === 'sale') return item.payment_status;
    if (item.type === 'invoice') return item.payment_status;
    return null;
  }, []);

  // دالة لعرض حالة المنتج المركب في نتائج البحث
  const getProductDisplay = useCallback((item: any) => {
    if (item.type === 'product' && item.product_type === 'recipe') {
      return {
        quantity: item.is_active ? 'متوفر' : 'غير متوفر',
        quantityColor: item.is_active ? '#4CAF50' : '#f44336'
      };
    }
    if (item.type === 'product') {
      return {
        quantity: item.stock,
        quantityColor: item.stock === 0 ? '#f44336' : '#4CAF50'
      };
    }
    return {
      quantity: null,
      quantityColor: null
    };
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: '100px',
      zIndex: 2000,
      animation: 'fadeIn 0.2s ease'
    }}>
      <div
        ref={modalRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '600px',
          maxWidth: '90%',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          animation: 'slideDown 0.3s ease'
        }}
      >
        {/* شريط البحث */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '15px 20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa'
        }}>
          <span style={{ fontSize: '20px', marginLeft: '10px', color: '#666' }}>🔍</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            placeholder="ابحث عن منتجات، فواتير، موردين..."
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              outline: 'none',
              fontSize: '16px',
              backgroundColor: 'transparent'
            }}
          />
          {loading && (
            <div style={{ marginLeft: '10px' }}>
              <div className="loading-spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }} />
            </div>
          )}
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              marginRight: '10px',
              color: '#999',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#f44336'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#999'}
          >
            ×
          </button>
        </div>

        {/* فلترة الأنواع */}
        <div style={{
          padding: '12px 15px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          backgroundColor: 'white'
        }}>
          {[
            { type: '', label: 'الكل', icon: '🔍' },
            { type: 'products', label: 'منتجات', icon: '📦' },
            { type: 'invoices', label: 'فواتير شراء', icon: '📄' },
            { type: 'sales', label: 'فواتير بيع', icon: '💰' },
            { type: 'suppliers', label: 'موردين', icon: '🤝' },
            { type: 'categories', label: 'تصنيفات', icon: '🏷️' },
            { type: 'users', label: 'مستخدمين', icon: '👤' }
          ].map(filter => (
            <button
              key={filter.type}
              onClick={() => handleTypeChange(filter.type)}
              style={{
                padding: '6px 14px',
                backgroundColor: selectedType === filter.type ? '#4CAF50' : '#f0f0f0',
                color: selectedType === filter.type ? 'white' : '#333',
                border: 'none',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: selectedType === filter.type ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (selectedType !== filter.type) {
                  e.currentTarget.style.backgroundColor = '#e0e0e0';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedType !== filter.type) {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }
              }}
            >
              <span>{filter.icon}</span>
              {filter.label}
            </button>
          ))}
        </div>

        {/* نتائج البحث */}
        <div style={{
          padding: '15px',
          overflowY: 'auto',
          maxHeight: '400px',
          backgroundColor: '#fafafa'
        }}>
          {!query && recentSearches.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>🕒</span>
                عمليات بحث سابقة
              </h4>
              {recentSearches.map((search, index) => (
                <div
                  key={index}
                  onClick={() => handleRecentSearchClick(search)}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    marginBottom: '5px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: '1px solid #eee',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#4CAF50';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#eee';
                  }}
                >
                  <span style={{ fontSize: '16px', color: '#666' }}>🕒</span>
                  <span style={{ color: '#333' }}>{search}</span>
                </div>
              ))}
            </div>
          )}

          {allResults.map((item, index) => {
            const price = getItemPrice(item);
            const status = getItemStatus(item);
            const display = getProductDisplay(item);
            
            return (
              <div
                key={`${item.type}-${item.id}`}
                id={`search-item-${index}`}
                onClick={() => handleResultClick(item)}
                style={{
                  padding: '12px',
                  backgroundColor: selectedIndex === index ? '#e3f2fd' : 'white',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: selectedIndex === index ? '2px solid #2196F3' : '1px solid #eee',
                  transition: 'all 0.2s',
                  boxShadow: selectedIndex === index ? '0 2px 8px rgba(33,150,243,0.2)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (selectedIndex !== index) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#4CAF50';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedIndex !== index) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#eee';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 2 }}>
                  <span style={{ fontSize: '20px' }}>{getTypeIcon(item.type)}</span>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '14px' }}>
                        {item.name_ar || item.name || item.sale_number || item.invoice_number}
                      </strong>
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '12px',
                        fontSize: '10px',
                        color: '#666'
                      }}>
                        {getTypeName(item.type)}
                      </span>
                    </div>
                    {(item.name_en || item.customer_name || item.supplier_name) && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
                        {item.name_en || item.customer_name || item.supplier_name}
                      </div>
                    )}
                    {item.type === 'product' && item.product_type === 'recipe' && (
                      <div style={{ fontSize: '11px', color: '#9C27B0', marginTop: '2px' }}>
                        منتج مركب
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {price !== null && (
                    <span style={{ color: '#4CAF50', fontWeight: 'bold', fontSize: '14px' }}>
                      {price} ج.م
                    </span>
                  )}
                  {status !== null && display.quantity !== null && (
                    <span style={{
                      padding: '3px 8px',
                      backgroundColor: display.quantityColor || (status > 0 ? '#4CAF50' : '#f44336'),
                      color: 'white',
                      borderRadius: '16px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      {display.quantity !== null ? display.quantity : status}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {query && !loading && allResults.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px', 
              color: '#666',
              backgroundColor: 'white',
              borderRadius: '8px'
            }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '10px' }}>🔍</span>
              <p style={{ fontSize: '16px', marginBottom: '5px' }}>لا توجد نتائج لـ "{query}"</p>
              <p style={{ fontSize: '13px', color: '#999' }}>حاول استخدام كلمات مختلفة أو تصفح الأقسام</p>
            </div>
          )}
        </div>

        {/* تلميحات لوحة المفاتيح */}
        <div style={{
          padding: '12px 15px',
          borderTop: '1px solid #eee',
          fontSize: '11px',
          color: '#999',
          display: 'flex',
          gap: '20px',
          backgroundColor: 'white'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ padding: '2px 5px', backgroundColor: '#f0f0f0', borderRadius: '3px' }}>↵</span>
            Enter للبحث
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ padding: '2px 5px', backgroundColor: '#f0f0f0', borderRadius: '3px' }}>esc</span>
            للإغلاق
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ padding: '2px 5px', backgroundColor: '#f0f0f0', borderRadius: '3px' }}>↑ ↓</span>
            للتنقل
          </span>
        </div>
      </div>

      {/* إضافة الأنيمشن */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .loading-spinner {
          border: 2px solid #f3f3f3;
          border-top: 2px solid #2196F3;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default SearchComponent;